import React, { useMemo } from 'react';
import { Accordion, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { FolderSelect } from 'components/forms/FolderSelect';

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

interface Props {
  name: string;
  setName: (v: string) => void;
  timezone: string;
  setTimezone: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  zone: string;
  setZone: (v: string) => void;
  folderId: number | null;
  setFolderId: (v: number | null) => void;
  zones: any[];
  isCalendar: boolean;
  setIsCalendar: (v: boolean) => void;
  dayRollover: string | '';
  setDayRollover: (v: string | '') => void;
  minOpenValue: number | '';
  setMinOpenValue: (v: number | '') => void;
  minOpenUnit: 'minutes' | 'hours';
  setMinOpenUnit: (v: 'minutes' | 'hours') => void;
}

const BasicInfo: React.FC<Props> = ({ name, setName, timezone, setTimezone, description, setDescription, zone, setZone, folderId, setFolderId, zones }) => {
  const { t } = useTranslation();

  const timezoneOptions = useMemo(() => {
    const allTimezones = moment.tz.names();
    const browserTz = getBrowserTimezone();

    const priorityTimezones: string[] = ['UTC'];
    if (browserTz && browserTz !== 'UTC' && allTimezones.includes(browserTz)) {
      priorityTimezones.push(browserTz);
    }

    const remainingTimezones = allTimezones.filter((tz) => !priorityTimezones.includes(tz)).sort();

    return { priorityTimezones, remainingTimezones, browserTz };
  }, []);

  return (
    <Accordion defaultActiveKey="0" className="mb-3">
      <Accordion.Item eventKey="0">
        <Accordion.Header>{t('Basic Information')}</Accordion.Header>
        <Accordion.Body>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={2}>
              {t('Name')}
            </Form.Label>
            <Col sm={10}>
              <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={2}>
              {t('Timezone')}
            </Form.Label>
            <Col sm={10}>
              <Form.Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {timezoneOptions.priorityTimezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz === timezoneOptions.browserTz && tz !== 'UTC' ? `${tz} (Your timezone)` : tz}
                  </option>
                ))}
                {timezoneOptions.remainingTimezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Form.Select>
              <div className="text-muted small mt-1">Use IANA timezones for reliable DST handling.</div>
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={2}>
              {t('Description')}
            </Form.Label>
            <Col sm={10}>
              <Form.Control as="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={2}>
              {t('Zone')}
            </Form.Label>
            <Col sm={10}>
              <Form.Select value={zone} onChange={(e) => setZone(e.target.value)}>
                <option value="">{t('Select a Zone')}</option>
                {zones.map((z: any) => (
                  <option key={z.zone_id} value={z.zone_id}>
                    {z.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={2}>
              {t('Folder')}
            </Form.Label>
            <Col sm={10}>
              <FolderSelect value={folderId} onChange={setFolderId} zoneId={zone ? Number(zone) : null} />
            </Col>
          </Form.Group>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default BasicInfo;
